import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (res.ok) return;

  let errorMessage = res.statusText;
  const contentType = res.headers.get("content-type");

  try {
    const clonedRes = res.clone();

    if (contentType && contentType.includes("application/json")) {
      const json = (await clonedRes.json()) as any;
      const apiError = json?.error;
      errorMessage =
        typeof apiError === "string"
          ? apiError
          : apiError?.message || json?.message || JSON.stringify(json);
    } else {
      const text = await clonedRes.text();
      if (text.includes("<!DOCTYPE") || text.includes("<html")) {
        errorMessage = `서버 오류가 발생했습니다 (${res.status})`;
      } else {
        errorMessage = text.substring(0, 500);
      }
    }
  } catch {
    errorMessage = res.statusText || `서버 오류 (${res.status})`;
  }

  throw new Error(`${res.status}: ${errorMessage}`);
}

export function unwrapPayload<T = unknown>(payload: any, depth: number = 0): T {
  if (depth > 3) {
    return payload as T;
  }

  if (payload == null || typeof payload !== "object") {
    return payload as T;
  }

  if ("ok" in payload && payload.ok === true) {
    if ("data" in payload) {
      return unwrapPayload(payload.data, depth + 1) as T;
    }
    if ("item" in payload) {
      return unwrapPayload(payload.item, depth + 1) as T;
    }
    if ("items" in payload) {
      return unwrapPayload(payload.items, depth + 1) as T;
    }
  }

  if ("data" in payload) {
    return unwrapPayload(payload.data, depth + 1) as T;
  }
  if ("item" in payload) {
    return unwrapPayload(payload.item, depth + 1) as T;
  }
  if ("items" in payload) {
    return unwrapPayload(payload.items, depth + 1) as T;
  }

  return payload as T;
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { headers?: Record<string, string> } & Record<
    string,
    string | Record<string, string> | undefined
  >,
): Promise<Response> {
  const isFormData = data instanceof FormData;
  const { headers: nestedHeaders, ...flatHeaders } = options ?? {};
  const headers: Record<string, string> = {
    ...(flatHeaders as Record<string, string>),
    ...(nestedHeaders || {}),
  };

  if (!isFormData && data) {
    headers["Content-Type"] = "application/json";
  }

  await attachFirebaseAuthHeader(method, url, headers);

  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

async function attachFirebaseAuthHeader(
  method: string,
  url: string,
  headers: Record<string, string>,
): Promise<void> {
  if (headers.Authorization || headers.authorization) return;

  const normalizedMethod = method.toUpperCase();
  if (["GET", "HEAD", "OPTIONS"].includes(normalizedMethod)) return;
  if (!isSameOriginApiUrl(url)) return;

  try {
    const { getIdToken } = await import("@/lib/firebase");
    const token = await getIdToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    // Public mutation endpoints may still be valid without authentication. The
    // API remains responsible for returning 401 when authentication is required.
    console.warn("[apiRequest] Unable to attach Firebase ID token", error);
  }
}

function isSameOriginApiUrl(url: string): boolean {
  if (url.startsWith("/api/")) return true;
  if (typeof window === "undefined") return false;

  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith("/api/")
    );
  } catch {
    return false;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const url = queryKey[0] as string;
    const res = await fetch(url, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    const json = await res.json();
    const data = unwrapPayload(json);
    return data as any;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
